import {Component, DestroyRef, inject} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators} from '@angular/forms';
import {MatFormField, MatLabel, MatError} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {MatCard, MatCardContent} from '@angular/material/card';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {HttpErrorResponse} from '@angular/common/http';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TranslocoModule, TranslocoService} from '@jsverse/transloco';
import {PasswordResetService} from '../../services/password-reset.service';

// Backend error CODE → Transloco key (AC2). Codes mirror the previous
// ERROR_MESSAGES map (password-reset/confirm Lambda); resolved via
// TranslocoService.translate() so the displayed message localizes.
const ERROR_KEYS: Record<string, string> = {
  invalid_token: 'resetPassword.errors.invalidToken',
  weak_password: 'resetPassword.errors.weakPassword',
  invalid_request: 'common.errors.invalidRequest',
  server_error: 'common.errors.serverError',
};

// Group-level validator: confirmPassword must equal password.
function passwordsMatch(group: AbstractControl): ValidationErrors | null {
  const pw = group.get('password')?.value;
  const confirm = group.get('confirmPassword')?.value;
  return pw === confirm ? null : {passwordMismatch: true};
}

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormField, MatLabel, MatError, MatInput,
    MatButton, MatIconButton, MatIcon,
    MatCard, MatCardContent,
    RouterLink,
    TranslocoModule,
  ],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.css',
})
export class ResetPasswordComponent {
  resetForm: FormGroup;
  error: string | null = null;
  loading = false;
  hidePassword = true;
  hideConfirm = true;
  // After a successful confirm we show a receipt screen (NOT an auto-redirect) —
  // the user must see that their password changed and click "Return to app".
  resetComplete = false;
  protected token = '';
  protected clientId = '';
  protected appUrl = ''; // application entry from the confirm response; '' → /login fallback
  protected appName = ''; // Client display name for the "Return to <appName>" button

  private readonly passwordResetService = inject(PasswordResetService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translocoService = inject(TranslocoService);

  constructor() {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.token = params['token'] || '';
      // client_id is NOT read from the URL — the reset link carries only the
      // token. The backend derives client_id from the token and returns it on a
      // successful confirm (used for the post-reset /login redirect).
    });
    this.resetForm = new FormGroup({
      password: new FormControl('', [Validators.required, Validators.minLength(12), Validators.maxLength(72)]),
      confirmPassword: new FormControl('', [Validators.required]),
    }, {validators: passwordsMatch});
  }

  submit(): void {
    if (this.resetForm.invalid || this.loading) {
      this.resetForm.markAllAsTouched();
      return;
    }
    if (!this.token) {
      this.error = this.translocoService.translate('resetPassword.errors.invalidToken');
      return;
    }
    this.loading = true;
    this.error = null;
    const newPassword = this.resetForm.get('password')!.value;
    this.passwordResetService.confirmReset(this.token, newPassword).subscribe({
      next: (res) => {
        this.loading = false;
        // Show a success receipt — do NOT auto-redirect. Capture the application
        // entry (preferred) + the token-derived client_id (fallback) for the
        // "Return to app" button.
        this.appUrl = res?.redirect_url ?? '';
        this.appName = res?.app_name ?? '';
        this.clientId = res?.client_id ?? this.clientId;
        this.resetComplete = true;
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        const code = err.error?.error || 'server_error';
        this.error = this.translocoService.translate(ERROR_KEYS[code] ?? ERROR_KEYS['server_error']);
      },
    });
  }

  // "Return to app" button on the success receipt. Prefers the application's own
  // entry (re-initiates a fresh OAuth login, since the original authorize params
  // are gone); falls back to the login page with the token-derived client_id.
  returnToApp(): void {
    if (this.appUrl) {
      this.redirectToApp(this.appUrl);
      return;
    }
    this.router.navigate(['/'], {queryParams: {client_id: this.clientId, reset: 'ok'}});
  }

  // Full-page navigation to the application (external to this SPA). Wrapped so
  // tests can assert it without stubbing window.location.
  protected redirectToApp(url: string): void {
    window.location.href = url;
  }
}
